import { memo, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@teo/widget-shared';
import {
  isImageDebugEnabled,
  logImage,
  resolveImageDisplayUrl,
  shortImageUrl,
} from '@teo/widget-core';
import { useWidget } from '../context/WidgetContext.js';
import { useRichMediaOptional } from './RichMediaContext.js';
import { MessageStatusIcon } from '../components/MessageStatusIcon.js';
import { IconRetry } from '../components/icons.js';

type OutgoingImageBubbleProps = {
  message: ChatMessage;
  timeLabel: string;
  messageKey: string;
  onRetry?: (clientId: string) => void;
  onMediaLoad?: () => void;
};

export const OutgoingImageBubble = memo(function OutgoingImageBubble({
  message,
  timeLabel,
  messageKey,
  onRetry,
  onMediaLoad,
}: OutgoingImageBubbleProps) {
  const { t } = useWidget();
  const richMedia = useRichMediaOptional();
  const serverUrl = message.mediaUrl ?? message.attachment?.url;
  const displayUrl = resolveImageDisplayUrl(
    message.clientId,
    message.id,
    serverUrl,
  );
  const isSending = message.status === 'sending';
  const fileName = message.attachment?.fileName;
  const lastLoggedSrcRef = useRef<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  if (isImageDebugEnabled()) {
    if (displayUrl && lastLoggedSrcRef.current !== displayUrl) {
      logImage('ui', 'img src applied', {
        messageKey,
        from: shortImageUrl(lastLoggedSrcRef.current),
        to: shortImageUrl(displayUrl),
        status: message.status,
      });
      lastLoggedSrcRef.current = displayUrl;
    }
  }

  useEffect(() => {
    if (!isImageDebugEnabled()) return;
    logImage('ui', 'OutgoingImageBubble mount', { messageKey });
    return () => {
      logImage('ui', 'OutgoingImageBubble unmount', { messageKey });
    };
  }, [messageKey]);

  const openPreview = () => {
    if (!displayUrl || isSending || !richMedia) return;
    richMedia.openLightbox({
      kind: 'image',
      url: displayUrl,
      title: fileName,
    });
  };

  return (
    <div className="teo-outgoing-media">
      <button
        type="button"
        className={[
          'teo-outgoing-media__btn',
          'teo-outgoing-media__btn--media',
          loaded ? '' : 'teo-media-skeleton',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={openPreview}
        disabled={!displayUrl || isSending || !richMedia}
        aria-label={fileName ?? 'Image'}
      >
        {displayUrl ? (
          <img
            className="teo-outgoing-media__img"
            src={displayUrl}
            alt={fileName ?? ''}
            loading="eager"
            decoding="async"
            onLoad={() => {
              setLoaded(true);
              logImage('ui', 'img onLoad', {
                messageKey,
                url: shortImageUrl(displayUrl),
              });
              onMediaLoad?.();
            }}
            onError={() => {
              // Clear skeleton so failed/HEIC previews do not shimmer forever.
              setLoaded(true);
              logImage(
                'ui',
                'img onError',
                { messageKey, url: shortImageUrl(displayUrl) },
                'error',
              );
            }}
          />
        ) : null}
        {isSending && (
          <span className="teo-outgoing-media__progress" aria-hidden>
            <span className="teo-bubble__image-progress-ring" />
          </span>
        )}
      </button>
      <div className="teo-bubble__meta teo-bubble__meta--media-footer">
        <time dateTime={message.createdAt}>{timeLabel}</time>
        {message.status && (
          <MessageStatusIcon
            status={message.status}
            sendError={message.sendError}
          />
        )}
        {message.status === 'failed' && onRetry && (
          <button
            type="button"
            className="teo-retry-btn"
            onClick={() => onRetry(messageKey)}
            aria-label={t.retrySend}
          >
            <IconRetry />
          </button>
        )}
      </div>
    </div>
  );
}, outgoingImagePropsEqual);

function outgoingImagePropsEqual(
  prev: OutgoingImageBubbleProps,
  next: OutgoingImageBubbleProps,
): boolean {
  return (
    prev.message === next.message &&
    prev.timeLabel === next.timeLabel &&
    prev.messageKey === next.messageKey &&
    prev.onRetry === next.onRetry &&
    prev.onMediaLoad === next.onMediaLoad
  );
}
